import type { AuthError, AuthenticationResult, ProtocolMode } from "@azure/msal-browser-1p";
import type { InteractionType } from "./InteractionType";
import type { AccountSourceType } from "owa-account-source-list-types";
import { getClientId, getProtocolMode } from "../initializeMsalLibrary";
import { getHttpStatusFromError } from "./getHttpStatusFromError";
import { getApp, isHosted, getHostedScenario, getHostHub, getOpxHostApp, scrubForPii, } from "owa-config";
import type { App } from "owa-config-types";
import { getMsalVersion } from "../getMsalVersion";

export interface AuthCommonData {
    accountType: AccountSourceType | undefined;
    app: App;
    applicationId: string;
    correlationId: string;
    hostApp: string;
    hostedScenario: string;
    interactionType: InteractionType;
    isHosted: boolean;
    isSuccess: boolean;
    isValidAuthorityUrl: boolean | undefined;
    latency: number;
    msalVersion: string;
    pageVisible: boolean | undefined;
    isAccountPresent?: boolean;
    scopes?: string;
    protocolMode?: ProtocolMode;
}

export interface AuthResultData extends AuthCommonData {
    fromCache: boolean;
    requestedScope: string | undefined;
}

export interface AuthErrorData extends AuthCommonData {
    errorCode: string;
    errorMessage: string;
    authHttpStatus: number | undefined;
    isCAERemediationRequired: boolean | undefined;
    isSigninBannerShown: boolean | undefined;
    subError: string;
    incomingApiName: string;
}

export function getLogAuthResultData(interactionType: InteractionType, authResult: AuthenticationResult, authRequestStartTime: number, typeHint?: AccountSourceType, isUrlPresent?: boolean, isPageVisible?: boolean, requestedScope?: string, isAccountPresent?: boolean): AuthResultData {
    return {
        interactionType,
        isSuccess: authResult.accessToken.length > 0 ? true : false,
        latency: self.performance.now() - authRequestStartTime,
        accountType: typeHint,
        isValidAuthorityUrl: isUrlPresent,
        correlationId: authResult.correlationId,
        scopes: authResult.scopes.join(" "),
        fromCache: authResult.fromCache,
        pageVisible: isPageVisible,
        isAccountPresent,
        requestedScope,
        applicationId: getClientId(),
        msalVersion: getMsalVersion(),
        isHosted: isHosted(),
        app: getApp(),
        hostedScenario: getHostedScenario(),
        hostApp: getHostHub() || getOpxHostApp(),
        protocolMode: getProtocolMode(),
    };
}

export function getLogAuthErrorData(interactionType: InteractionType, e: AuthError, authRequestStartTime: number, apiName: string, scopes?: string[], typeHint?: AccountSourceType, isUrlPresent?: boolean, isCAERemediationRequired?: boolean, isPageVisible?: boolean, isSigninRequired?: boolean, isAccountPresent?: boolean): AuthErrorData {
    return {
        interactionType,
        isSuccess: false,
        latency: self.performance.now() - authRequestStartTime,
        accountType: typeHint,
        scopes: scopes?.join(" "),
        correlationId: e.correlationId,
        errorCode: e.errorCode,
        errorMessage: scrubForPii(e.errorMessage) ?? "",
        authHttpStatus: getHttpStatusFromError(e),
        subError: e.subError,
        isValidAuthorityUrl: isUrlPresent,
        isCAERemediationRequired,
        pageVisible: isPageVisible,
        isAccountPresent,
        applicationId: getClientId(),
        isSigninBannerShown: isSigninRequired,
        msalVersion: getMsalVersion(),
        isHosted: isHosted(),
        app: getApp(),
        hostedScenario: getHostedScenario(),
        hostApp: getHostHub() || getOpxHostApp(),
        protocolMode: getProtocolMode(),
        incomingApiName: apiName,
    };
}
